import { Box, Card, IconButton, Skeleton, Stack, StackProps, Typography } from '@mui/material';
import { PeriodTypeEnum, TimetableStructure } from '@schooly/api';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { EditIcon, TypographyWithOverflowHint } from '@schooly/style';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { StructurePeriodsGrid } from './StructurePeriodsGrid';
import { getAgeGroupNames } from './utils';

interface SchoolTimetableStructureCardProps {
  structure: TimetableStructure;
  onEdit?: () => void;
  schoolId: string;
}

export const SchoolTimetableStructureCard: FC<SchoolTimetableStructureCardProps> = ({
  structure,
  onEdit,
  schoolId,
}) => {
  const { formatMessage } = useIntl();

  const {
    getAgeGroupById,
    getSchoolLevelById,
    schoolLevelsWithAgeGroupsMap,
    isLoading: isLoadingAgeGroups,
  } = useAgeGroups({
    schoolId,
    userType: SchoolUserRole.Student,
  });

  const ageGroupNames = useMemo(
    () =>
      getAgeGroupNames({
        ageGroupIds: structure.age_group_ids,
        getAgeGroupById,
        getSchoolLevelById,
        schoolLevelsWithAgeGroupsMap,
      }),
    [structure.age_group_ids, getAgeGroupById, getSchoolLevelById, schoolLevelsWithAgeGroupsMap],
  );
  const daysCount = structure.weekdays.length;
  const lessonsCount = structure.periods.filter(
    (period) => period.type === PeriodTypeEnum.Lesson,
  ).length;

  return (
    <Card sx={{ overflow: 'auto' }} data-scope>
      <Box sx={{ minWidth: 'fit-content' }}>
        <Stack
          direction="row"
          gap={2}
          sx={(theme) => ({ borderBottom: theme.mixins.borderValue(), p: 2.5 })}
        >
          <Stack direction="row" gap={2} flex={1} overflow="hidden">
            <TimetableStructureCardItem
              label={formatMessage({ id: 'school-timetableStructure-Name' })}
              value={structure.name}
              sx={{ flex: '0 1 200px', overflow: 'hidden' }}
            />

            <TimetableStructureCardItem
              label={formatMessage({ id: 'school-timetableStructure-AgeGroups' })}
              value={ageGroupNames || '-'}
              isLoading={isLoadingAgeGroups}
              sx={{ flex: '0 1 250px', overflow: 'hidden' }}
            />

            <TimetableStructureCardItem
              label={formatMessage({ id: 'school-timetableStructure-Cycle' })}
              value={formatMessage(
                { id: 'school-timetableStructure-DaysAndLessons' },
                { days: daysCount, lessons: lessonsCount },
              )}
              sx={{ overflow: 'hidden' }}
            />
          </Stack>

          {onEdit && (
            <IconButton onClick={onEdit} inverse>
              <EditIcon />
            </IconButton>
          )}
        </Stack>

        <Box p={2.5}>
          <StructurePeriodsGrid
            weekdays={structure.weekdays}
            periods={structure.periods}
            timeSlots={structure.time_slots}
          />
        </Box>
      </Box>
    </Card>
  );
};

type TimetableStructureCardItemProps = {
  label: string;
  value: string;
  isLoading?: boolean;
} & StackProps;

const TimetableStructureCardItem: FC<TimetableStructureCardItemProps> = ({
  label,
  value,
  isLoading,
  ...props
}) => {
  return (
    <Stack {...props}>
      <Typography color="text.secondary" noWrap>
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" width={120} />
      ) : (
        <TypographyWithOverflowHint variant="h2">{value}</TypographyWithOverflowHint>
      )}
    </Stack>
  );
};
